import React, { useEffect, useRef } from 'react'
import { Deal } from '../models/shared/Deal'
import { FormControl, Button } from 'react-bootstrap'
import { useText } from '../context/TextContext'
import AddressSearch from './AddressSearch'
import MapDealView from './MapDealView'
import TagsManagement from './TagsManagement'
import MembersManagement from './MembersManagement'
import CommentsSection from './CommentsSection'
import DealSteps from './DealSteps'
import DealAuditLog from './DealAuditLog'
import Distance from './Distance'
import { navigateOnMap } from '../utils/logic'
import DealAttachments from './DealAttachments'
import { Attachement } from '../models/shared/Attachment'
import CustomerSearch from './CustomerSearch'
import DealOfferSection from './DealOfferSection'
import DealInvoiceSection from './DealInvoiceSection'
import { useOrganization } from '../context/OrganizationContext'
import { MARKETING_WEBSITE, PATHS } from '../utils/shared/constants'
import { toast } from 'react-toastify'
import { useSaveToast } from '../context/SaveToastContext'
import CloseButton from './CloseButton'
import DealModalActions from './modals/DealModalActions'
import { Link } from 'react-router-dom'
import BrandName from './BrandName'
import DealDueDate from './DealDueDate'
import { deleteField } from 'firebase/firestore'
import { Offer } from '../models/shared/Offer'
import { Invoice } from '../models/shared/Invoice'
import { useUploadDealAttachment } from '../hooks/useUploadDealAttachement'
import DealHoursSection from './DealHoursSection'
import RiskAssessmentSection from './RiskAssessmentSection'
import { UserRole } from '../models/shared/UserInvitation'

interface DealContentProps {
    deal: Deal;
    offers?: Offer[];
    invoices?: Invoice[];
    initialFocus?: 'name';
    readOnly?: boolean;

    onDealUpdate?: (update: Partial<Deal>, options: any) => Promise<void>;
    onClose?: () => void;
    onDealArchive?: (deal: Deal) => void;
    onDealStepChange?: (dealId: string, stepId: string) => Promise<void>;

    className?: string;
}

export default function DealContent(props: DealContentProps) {
    const { deal: upToDateDeal, readOnly } = props
    const dealId = upToDateDeal.id || '';
    const text = useText();
    const { organization } = useOrganization();
    const nameRef = useRef<HTMLInputElement>(null);
    const [isHoveringCount, setIsHoveringCount] = React.useState(0);
    const { markSaved, startSaving, stopSaving } = useSaveToast();
    const [deal, setDeal] = React.useState<Deal>(upToDateDeal);
    const { orgId, orgRole } = useOrganization();
    const isWorker = orgRole === UserRole.WORKER;
    const [focusOnDueDate, setFocusOnDueDate] = React.useState(false);
    const uploadDealAttachment = useUploadDealAttachment(orgId);

    useEffect(() => {
        if (props.initialFocus === 'name') {
            nameRef.current?.focus()
            nameRef.current?.select()
        }
    }, [props.initialFocus])

    const updateDeal = async (update: Partial<Deal>, options: any = {}) => {
        const newDeal = { ...deal, ...update };
        delete newDeal.brandNew;
        setDeal(newDeal)

        if (props.onDealUpdate) {
            const update = {
                ...newDeal
            }
            if (update.dueAt == null) {
                (update as any).dueAt = deleteField();
            }
            await props.onDealUpdate(update, options);
        }
    }

    const updateDealStep = async (stepId: string) => {
        setDeal({ ...deal, stepId, status: 'active' })

        if (props.onDealStepChange) {
            await props.onDealStepChange(dealId, stepId);
        } else {
            updateDeal({ stepId, status: 'active' })
        }
    }

    const uploadAttachments = async (files: File | FileList) => {
        try {
            startSaving();
            const fileArray = files instanceof FileList ? Array.from(files) : [files];

            const newAttachments: Attachement[] = deal.attachments || [];
            for (const file of fileArray) {
                const newAttachement = await uploadDealAttachment(dealId, file);
                if (newAttachement) {
                    newAttachments.push(newAttachement);
                } else {
                    toast.error(text.defaultError);
                    stopSaving();
                    console.error('Error uploading file', file.name);
                    return;
                }
            }

            if (!newAttachments.length) {
                stopSaving();
                return;
            }

            await updateDeal({ attachments: newAttachments }, { noSaveToast: true });

            markSaved();
        } catch (error) {
            console.error('Error uploading file', error);
            stopSaving();
            toast.error(text.defaultError);
        }
    }

    const handleNewAttachment = (capture: boolean = false) => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.multiple = true;

        if (capture) {
            fileInput.accept = "image/*"
            fileInput.capture = "environment"
        }

        fileInput.onchange = (e) => {
            const files = (e.target as HTMLInputElement).files;
            if (!files || files.length === 0) {
                return;
            }

            uploadAttachments(files);
        }

        fileInput.click();
    }

    const handleNewDueDate = () => {
        setFocusOnDueDate(true);
        updateDeal({ dueAt: new Date() })
    }

    const handleDragEnter = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        setIsHoveringCount(current => current + 1);
    }

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsHoveringCount(current => current - 1);
    }

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer?.files?.[0];
        if (file) {
            uploadAttachments(file);
        }
        setIsHoveringCount(0);
    }


    const isArchived = deal.status === 'archived';
    const showAddressSection = deal.place || !readOnly;
    const placeBackgroundColor = organization.dealSteps?.find(s => s.id === deal.stepId)?.color;
    return (
        <div
            className='d-flex flex-column gap-4 pb-4 p-0'
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={e => e.preventDefault()}
            onDrop={handleDrop}
        >
            <div className={`px-4 pt-4 rounded-top d-flex flex-column gap-4 ${isArchived && 'bg-warning'}`}>

                <div className='d-flex justify-content-between align-items-center'>
                    {props.onClose
                        ? <CloseButton onClick={props.onClose} />
                        : <Link to={MARKETING_WEBSITE}>
                            <BrandName />
                        </Link>
                    }

                    {!readOnly && <DealModalActions deal={upToDateDeal}
                        offers={props.offers}
                        onNewAttachment={handleNewAttachment}
                        onNewDueDate={handleNewDueDate}
                        onDealArchive={props.onDealArchive || (() => { })}
                    />}

                </div>

                {upToDateDeal && !readOnly && <DealSteps className="flex-1" deal={deal} onDealStepUpdate={updateDealStep} upToDateDeal={upToDateDeal} />}

                {isArchived && <div className='text-center text-muted p-2'><i className='bi bi-trash me-2' />{text.archived}</div>}
            </div>

            {organization.features?.hoursTracking && <DealHoursSection deal={deal} />}

            {organization.features?.riskAssessment && <RiskAssessmentSection className='px-4' deal={deal} onDealUpdate={updateDeal} readOnly={readOnly} />}

            {deal.dueAt && <div className='d-flex px-4'> <DealDueDate
                deal={deal} onDealUpdate={updateDeal}
                readOnly={readOnly}
                focus={focusOnDueDate} /></div>}

            <div className='px-4'>
                <h6>{text.name}</h6>
                <FormControl readOnly={readOnly} ref={nameRef} value={deal.name} onChange={(e) => updateDeal({ name: e.target.value })} />
            </div>

            <div className='px-4'>
                <h6>{text.description}</h6>
                <textarea onChange={(e) => updateDeal({ description: e.target.value })}
                    readOnly={readOnly}
                    value={deal.description || ''} className='form-control' rows={5} />
            </div>

            <CommentsSection className='px-4' deal={deal} onDealUpdate={updateDeal} readOnly={readOnly} />

            <CustomerSearch className='px-4'
                dealId={deal.id || ''}
                readOnly={readOnly}
                currentCustomerId={deal.customerId} onCustomerSelect={(customerId) => updateDeal({ customerId })}
            />

            <TagsManagement className='px-4' deal={deal} onDealUpdate={updateDeal} readOnly={readOnly} />

            <MembersManagement className='px-4' deal={deal} onDealUpdate={updateDeal} readOnly={readOnly} />

            {!readOnly && !isWorker &&
                <DealOfferSection className='px-4' offers={props.offers || []} deal={deal} />
            }

            {!readOnly &&
                <DealInvoiceSection className='px-4' invoices={props.invoices || []} deal={deal} />
            }

            {showAddressSection && <div className='px-4 d-flex flex-column gap-2'>
                <h6 className='m-0'>{text.address}</h6>
                <AddressSearch place={deal.place} onPlaceChange={place => updateDeal({ place, placeDistance: null })} readOnly={readOnly} />
                <Distance deal={upToDateDeal} onForceUpdate={() => updateDeal({})} />
                <MapDealView readonly={readOnly}
                    onPlaceChange={place => updateDeal({ place, placeDistance: null })}
                    placeGeocoding={deal.place?.geocoding}
                    orgGeocoding={organization.place?.geocoding}
                    placeBackgroundColor={placeBackgroundColor} />
                {deal.place?.geocoding && <Button onClick={() => navigateOnMap(deal.place?.geocoding)} variant='secondary'>{text.navigate}</Button>}
            </div>}

            <DealAttachments className='px-4' deal={deal} onDealUpdate={updateDeal} onNewAttachment={handleNewAttachment} readOnly={props.readOnly} />

            {upToDateDeal && <div className='px-4 d-flex flex-column gap-2'>
                <DealAuditLog deal={upToDateDeal} />
                <Link to={`${PATHS.DEAL_TIMELINE}/${deal.id}`}>
                    <div>{text.viewTimeline}</div>
                </Link>
            </div>}

            {!!isHoveringCount && <div className=' text-white position-absolute w-100 h-100 bg-dark bg-opacity-50 center flex-column'>
                <i className='bi bi-cloud-arrow-up fs-1 ' />
                <p>{text.dropFileHere}</p>
            </div>}
        </div>
    )
}
